// tabs/AccountsTab.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Users,
  Globe2,
  PieChart,
  Activity,
  Inbox,
  CheckCircle2,
  Circle,
  MapPin, // New icon for geography
} from "lucide-react";

/* --- THEME CONFIGURATION --- */
const theme = {
  colors: {
    brand: "#0056D2",
    surface: "#F8FAFC",
    border: "#E2E8F0",
    textMain: "#0F172A",
    textMuted: "#64748B",
    accent: "#0369A1",
    highlight: "#F0F9FF",
    success: "#10B981",
  },
};

/* --- PRE-LAUNCH TARGET LISTS --- */
// (Same as provided in your snippet)
const SEA_FIN = [
  { name: "BDO Unibank", domain: "bdo.com.ph", country: "Philippines" },
  {
    name: "METROPOLITAN BANK & TRUST COMPANY",
    domain: "metrobank.com.ph",
    country: "Philippines",
  },
  {
    name: "Top Frontier",
    domain: "topfrontier.com.ph",
    country: "Philippines",
  },
  { name: "Security Bank", domain: "securitybank.com", country: "Philippines" },
  {
    name: "Philippine National Bank",
    domain: "pnb.com.ph",
    country: "Philippines",
  },
  {
    name: "China Banking Corporation",
    domain: "chinabank.ph",
    country: "Philippines",
  },
  {
    name: "UnionBank of the Philippines",
    domain: "unionbankph.com",
    country: "Philippines",
  },
  {
    name: "Land Bank of the Philippines",
    domain: "landbank.com",
    country: "Philippines",
  },
  {
    name: "Home Credit Philippines",
    domain: "homecredit.ph",
    country: "Philippines",
  },
  {
    name: "Development Bank of the Philippines",
    domain: "dbp.ph",
    country: "Philippines",
  },
  {
    name: "S&P Global Market Intelligence Inc.",
    domain: "spglobal.com",
    country: "Philippines",
  },
  {
    name: "BANK OF THE PHILIPPINE ISLANDS",
    domain: "bpiexpressonline.com",
    country: "Philippines",
  },
  {
    name: "SM Investments Corporation",
    domain: "sminvestments.com",
    country: "Philippines",
  },
  {
    name: "PUBLIC BANK BHD",
    domain: "publicbankgroup.com",
    country: "Malaysia",
  },
  { name: "HONG LEONG BANK BERHAD", domain: "hlb.com.my", country: "Malaysia" },
  { name: "RHB BANK BERHAD", domain: "rhbgroup.com", country: "Malaysia" },
  {
    name: "Bank Simpanan Nasional",
    domain: "mybsn.com.my",
    country: "Malaysia",
  },
  {
    name: "MALAYAN BANKING BERHAD (Maybank)",
    domain: "maybank.com",
    country: "Malaysia",
  },
  { name: "CIMB Bank Group", domain: "cimb.com", country: "Malaysia" },
  { name: "Sime Darby", domain: "simedarby.com", country: "Malaysia" },
  {
    name: "PT BANK NEGARA INDONESIA (PERSERO) Tbk",
    domain: "bni.co.id",
    country: "Indonesia",
  },
  { name: "Bank Mandiri", domain: "bankmandiri.co.id", country: "Indonesia" },
  {
    name: "PT. BANK PERMATA TBK",
    domain: "permatabank.com",
    country: "Indonesia",
  },
  {
    name: "PT. BANK RAKYAT INDONESIA (PERSERO) TBK",
    domain: "bri.co.id",
    country: "Indonesia",
  },
  {
    name: "PT Bank Central Asia Tbk",
    domain: "bca.co.id",
    country: "Indonesia",
  },
  { name: "PT. BANK BTPN TBK", domain: "btpn.com", country: "Indonesia" },
  {
    name: "PT. BANK TABUNGAN NEGARA (PERSERO) TBK",
    domain: "btn.co.id",
    country: "Indonesia",
  },
  { name: "Bank BJB", domain: "bankbjb.co.id", country: "Indonesia" },
  { name: "Bank Pan Indonesia", domain: "panin.co.id", country: "Indonesia" },
  { name: "Bank Mega", domain: "bankmega.com", country: "Indonesia" },
  { name: "DBS Bank", domain: "dbs.com.sg", country: "Singapore" },
  {
    name: "United Overseas Bank (UOB)",
    domain: "uob.com.sg",
    country: "Singapore",
  },
  { name: "OCBC", domain: "ocbc.com", country: "Singapore" },
  {
    name: "Australia and New Zealand Banking Group (ANZ)",
    domain: "anz.com.au",
    country: "Australia",
  },
  {
    name: "Commonwealth Bank of Australia",
    domain: "commbank.com.au",
    country: "Australia",
  },
  {
    name: "Westpac Banking Corporation",
    domain: "westpac.com",
    country: "Australia",
  },
  {
    name: "National Australia Bank Limited",
    domain: "nab.com.au",
    country: "Australia",
  },
  {
    name: "Macquarie Group Limited",
    domain: "macquarie.com",
    country: "Australia",
  },
  {
    name: "Suncorp Group Limited",
    domain: "suncorp.com.au",
    country: "Australia",
  },
  {
    name: "Insurance Australia Group Limited",
    domain: "iag.com.au",
    country: "Australia",
  },
  { name: "Techcombank", domain: "techcombank.com", country: "Vietnam" },
  {
    name: "Bank for Investment and Development (BIDV)",
    domain: "bidv.com.vn",
    country: "Vietnam",
  },
  { name: "VP Bank", domain: "vpbank.com.vn", country: "Vietnam" },
  {
    name: "BV Bank (Viet Capital)",
    domain: "bvbank.net.vn",
    country: "Vietnam",
  },
  {
    name: "Military Commercial Joint Stock Bank (MB Bank)",
    domain: "mbbank.com.vn",
    country: "Vietnam",
  },
  { name: "Bangkok Bank", domain: "bangkokbank.com", country: "Thailand" },
  { name: "SCB X", domain: "scbx.com", country: "Thailand" },
  { name: "Krungthai Bank", domain: "krungthai.com", country: "Thailand" },
];

const INDIA_TECH = [
  { name: "Qualcomm India", domain: "qualcomm.com", country: "India" },
  { name: "Eureka Forbes", domain: "eurekaforbes.com", country: "India" },
  { name: "BSNL", domain: "bsnl.co.in", country: "India" },
  { name: "British Telecom India", domain: "bt.com", country: "India" },
  { name: "Mphasis", domain: "mphasis.com", country: "India" },
  { name: "Sonata Software", domain: "sonata-software.com", country: "India" },
  { name: "Infosys", domain: "infosys.com", country: "India" },
  { name: "PhonePe", domain: "phonepe.com", country: "India" },
  { name: "Brillio", domain: "brillio.com", country: "India" },
  { name: "Fujitsu", domain: "fujitsu.com", country: "India" },
  {
    name: "Manipal Technologies",
    domain: "manipaltechnologies.com",
    country: "India",
  },
  { name: "ACT Fibernet", domain: "actcorp.in", country: "India" },
  {
    name: "Delta Electronics India",
    domain: "deltaelectronicsindia.com",
    country: "India",
  },
  { name: "Qualitest", domain: "qualitestgroup.com", country: "India" },
  {
    name: "Tata Technologies",
    domain: "tatatechnologies.com",
    country: "India",
  },
  { name: "Tata Consultancy Services", domain: "tcs.com", country: "India" },
  { name: "Accenture", domain: "accenture.com", country: "India" },
  { name: "Sutherland", domain: "sutherlandglobal.com", country: "India" },
  { name: "ZS Associates", domain: "zs.com", country: "India" },
  { name: "Wipro Limited", domain: "wipro.com", country: "India" },
  { name: "Virtusa Limited", domain: "virtusa.com", country: "India" },
  { name: "Ingram Micro", domain: "ingrammicro.com", country: "India" },
  { name: "Hexaware Technologies", domain: "hexaware.com", country: "India" },
  { name: "Mahindra Comviva", domain: "comviva.com", country: "India" },
  { name: "Adobe India", domain: "adobe.com", country: "India" },
  { name: "Hinduja Group", domain: "hindujagroup.com", country: "India" },
  { name: "Piramal Group", domain: "piramal.com", country: "India" },
  { name: "Fiserv India", domain: "fiserv.com", country: "India" },
  { name: "Thermax Global", domain: "thermaxglobal.com", country: "India" },
  { name: "3i Infotech", domain: "3i-infotech.com", country: "India" },
  { name: "Merck India", domain: "merckgroup.com", country: "India" },
  { name: "SKF India", domain: "skf.com", country: "India" },
  { name: "Varroc", domain: "varroc.com", country: "India" },
  { name: "Cybage India", domain: "cybage.com", country: "India" },
  { name: "GlobalLogic India", domain: "globallogic.com", country: "India" },
  {
    name: "Publicis Groupe (Sapient)",
    domain: "publicissapient.com",
    country: "India",
  },
  { name: "Coforge", domain: "coforge.com", country: "India" },
  { name: "Sony India", domain: "sony.com", country: "India" },
];

const INDIA_FIN = [
  { name: "HDFC Bank", domain: "hdfcbank.com", country: "India" },
  { name: "Bank of Baroda", domain: "bankofbaroda.bank.in", country: "India" },
  { name: "IDFC First Bank", domain: "idfcfirstbank.com", country: "India" },
  { name: "Standard Chartered India", domain: "sc.bank.in", country: "India" },
  { name: "Muthoot Finance", domain: "muthootfinance.com", country: "India" },
  {
    name: "JPMorgan Chase India",
    domain: "jpmorganchase.com",
    country: "India",
  },
  { name: "State Street India", domain: "statestreet.com", country: "India" },
  { name: "Visa India Pvt. Ltd.", domain: "visa.co.in", country: "India" },
  { name: "PayPal India", domain: "paypal.com", country: "India" },
  { name: "YES BANK", domain: "yes.bank.in", country: "India" },
  { name: "Kotak Mahindra Bank", domain: "kotak.bank.in", country: "India" },
  { name: "State Bank of India", domain: "sbi.bank.in", country: "India" },
  { name: "Citi India", domain: "citigroup.com", country: "India" },
  {
    name: "Barclays Global Services (India)",
    domain: "barclays.bank.in",
    country: "India",
  },
];

export default function AccountsTab() {
  const [actuals, setActuals] = useState([]);
  const [view, setView] = useState("intelligence");

  useEffect(() => {
    fetch("/APAC_Reporting_India_SEA.csv")
      .then(async (res) => {
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        const rows = [];
        let cells = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const next = text[i + 1];

          if (char === '"') {
            if (inQuotes && next === '"') {
              current += '"';
              i++;
            } else {
              inQuotes = !inQuotes;
            }
          } else if (!inQuotes && char === ",") {
            cells.push(current.trim());
            current = "";
          } else if (
            !inQuotes &&
            (char === "\n" || (char === "\r" && next === "\n"))
          ) {
            if (char === "\r") i++;
            cells.push(current.trim());
            rows.push(cells);
            cells = [];
            current = "";
          } else {
            current += char;
          }
        }
        if (current || cells.length > 0) {
          cells.push(current.trim());
          rows.push(cells);
        }

        const validLeads = rows
          .slice(1)
          .map((row) => ({
            title: row[0],
            country: row[1],
            industry: row[3],
            priority: row[4],
            company: row[5],
            date: row[9],
          }))
          .filter((lead) => lead.date && lead.date.includes("/"));

        setActuals(validLeads);
      })
      .catch((err) => console.error("Account Parser Error:", err));
  }, []);

  const analytics = useMemo(() => {
    if (!actuals.length) return null;

    const accounts = {};
    const priorities = {};
    const countries = {};
    const allTargets = [...SEA_FIN, ...INDIA_TECH, ...INDIA_FIN];

    actuals.forEach((lead) => {
      // Aggregate Countries
      const country = lead.country || "Other";
      countries[country] = (countries[country] || 0) + 1;

      // Aggregate Accounts
      const comp = lead.company || "Unknown";
      if (!accounts[comp]) {
        accounts[comp] = {
          company: comp,
          leads: 0,
          functions: new Set(),
          signals: new Set(),
          isTarget: allTargets.some(
            (t) => t.name.toLowerCase() === comp.toLowerCase()
          ),
        };
      }
      accounts[comp].leads += 1;

      const t = (lead.title || "").toUpperCase();
      if (t.includes("HR")) accounts[comp].functions.add("HR");
      else if (t.includes("TALENT") || t.includes("L&D"))
        accounts[comp].functions.add("Talent");
      else accounts[comp].functions.add("Exec");

      let sig = lead.priority || "Other";
      if (sig.toLowerCase().includes("cost")) sig = "Cost Efficiency";
      if (sig.toLowerCase().includes("innovate")) sig = "Innovation Speed";
      if (sig.toLowerCase().includes("competitiveness"))
        sig = "Market Competitiveness";

      accounts[comp].signals.add(sig);
      priorities[sig] = (priorities[sig] || 0) + 1;
    });

    const sortedAccounts = Object.values(accounts).sort(
      (a, b) => b.leads - a.leads
    );

    const priorityList = Object.entries(priorities)
      .map(([theme, count]) => ({
        theme,
        count,
        pct: Math.round((count / actuals.length) * 100),
      }))
      .sort((a, b) => b.count - a.count);

    const countryList = Object.entries(countries)
      .map(([name, count]) => ({
        name,
        count,
        pct: Math.round((count / actuals.length) * 100),
      }))
      .sort((a, b) => b.count - a.count);

    return {
      totalAccounts: Object.keys(accounts).length,
      targetPenetration: sortedAccounts.filter((a) => a.isTarget).length,
      totalLeads: actuals.length,
      multiLeadPct: Math.round(
        (sortedAccounts.filter((a) => a.leads > 1).length /
          Object.keys(accounts).length) *
          100
      ),
      accountSummary: sortedAccounts,
      priorities: priorityList,
      countries: countryList,
    };
  }, [actuals]);

  if (!analytics)
    return (
      <div style={{ padding: 40, color: theme.colors.textMuted }}>
        Synchronizing Account Data...
      </div>
    );

  return (
    <div
      style={{
        backgroundColor: "#FFF",
        color: theme.colors.textMain,
        fontFamily: "Inter, sans-serif",
        padding: "40px",
      }}
    >
      {/* VIEW TOGGLE */}
      <div style={{ display: "flex", gap: 12, marginBottom: 32 }}>
        <button
          onClick={() => setView("intelligence")}
          style={{
            padding: "8px 20px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            background:
              view === "intelligence"
                ? theme.colors.brand
                : theme.colors.surface,
            color: view === "intelligence" ? "#fff" : theme.colors.textMuted,
            fontWeight: 600,
            fontSize: "13px",
            transition: "all 0.2s",
          }}
        >
          Market Intelligence Analysis
        </button>
        <button
          onClick={() => setView("coverage")}
          style={{
            padding: "8px 20px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            background:
              view === "coverage" ? theme.colors.brand : theme.colors.surface,
            color: view === "coverage" ? "#fff" : theme.colors.textMuted,
            fontWeight: 600,
            fontSize: "13px",
            transition: "all 0.2s",
          }}
        >
          Target Account Coverage
        </button>
      </div>

      {view === "intelligence" ? (
        <>
          {/* INTELLIGENCE HEADER */}
          <header
            style={{
              marginBottom: "40px",
              borderBottom: `1px solid ${theme.colors.border}`,
              paddingBottom: "24px",
            }}
          >
            <h1
              style={{
                fontSize: "26px",
                fontWeight: 800,
                margin: "0 0 8px 0",
                letterSpacing: "-0.5px",
              }}
            >
              Weekly Engagement Opt-in Analysis
            </h1>
            <p
              style={{
                color: theme.colors.textMuted,
                fontSize: "15px",
                maxWidth: "800px",
                lineHeight: "1.5",
              }}
            >
              Currently monitoring{" "}
              <span style={{ fontWeight: 600, color: theme.colors.textMain }}>
                {analytics.totalAccounts} unique accounts
              </span>
              . Distribution shows high density in{" "}
              <span style={{ fontWeight: 600, color: theme.colors.textMain }}>
                {analytics.countries[0]?.name}
              </span>
              .
            </p>
          </header>

          {/* METRIC GRID */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "20px",
              marginBottom: "40px",
            }}
          >
            <MetricCard
              label="Accounts Engaged"
              value={analytics.totalAccounts}
              icon={<Globe2 size={18} />}
            />
            <MetricCard
              label="Total Opt-ins"
              value={analytics.totalLeads}
              icon={<Inbox size={18} />}
            />
            <MetricCard
              label="Target Matches"
              value={analytics.targetPenetration}
              sub="Engaged pre-launch list"
              icon={<CheckCircle2 size={18} />}
            />
            <MetricCard
              label="Multi-Lead Growth"
              value={`${analytics.multiLeadPct}%`}
              sub="Accounts with 2+ stakeholders"
              icon={<Users size={18} />}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.5fr 1fr",
              gap: "32px",
            }}
          >
            <section>
              <h2 style={SectionHeaderStyle}>Top Performing Key Accounts</h2>
              <div style={ListWrapperStyle}>
                {analytics.accountSummary.slice(0, 10).map((acc, i) => (
                  <AccountRow key={i} acc={acc} i={i} total={10} />
                ))}
              </div>
            </section>
            <aside>
              {/* GEOGRAPHIC DISTRIBUTION CARD */}
              <div
                style={{
                  padding: "24px",
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: "20px",
                  marginBottom: 24,
                }}
              >
                <h2
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <MapPin size={20} color={theme.colors.brand} /> Geographic
                  Distribution
                </h2>
                {analytics.countries.map((c, i) => (
                  <div key={i} style={{ marginBottom: "16px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "12px",
                        marginBottom: "6px",
                        fontWeight: 600,
                      }}
                    >
                      <span>{c.name}</span>
                      <span style={{ color: theme.colors.brand }}>
                        {c.count} Leads
                      </span>
                    </div>
                    <div
                      style={{
                        height: "8px",
                        backgroundColor: theme.colors.surface,
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${c.pct}%`,
                          backgroundColor: theme.colors.brand,
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <PriorityCard priorities={analytics.priorities} />
              <InsightCard multiLeadPct={analytics.multiLeadPct} />
            </aside>
          </div>
        </>
      ) : (
        <>
          {/* COVERAGE VIEW */}
          <header style={{ marginBottom: "32px" }}>
            <h1 style={{ fontSize: "26px", fontWeight: 800, margin: 0 }}>
              Target Account Coverage Status
            </h1>
            <p
              style={{
                color: theme.colors.textMuted,
                fontSize: "15px",
                marginTop: 8,
              }}
            >
              Cross-referencing live leads against{" "}
              <span style={{ color: theme.colors.textMain, fontWeight: 600 }}>
                100+ named targets
              </span>
              .
            </p>
          </header>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 24,
            }}
          >
            <CoverageList
              title="SEA Financial Services"
              targets={SEA_FIN}
              actuals={analytics.accountSummary}
            />
            <CoverageList
              title="India Technology"
              targets={INDIA_TECH}
              actuals={analytics.accountSummary}
            />
            <CoverageList
              title="India Financial Services"
              targets={INDIA_FIN}
              actuals={analytics.accountSummary}
            />
          </div>
        </>
      )}
    </div>
  );
}

/* --- REUSABLE SUB-COMPONENTS --- */
// (MetricCard, AccountRow, CoverageList, PriorityCard, InsightCard as per original)

const MetricCard = ({ label, value, icon, sub }) => (
  <div
    style={{
      padding: "24px",
      backgroundColor: theme.colors.surface,
      borderRadius: "16px",
      border: `1px solid ${theme.colors.border}`,
      transition: "transform 0.2s",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        color: theme.colors.textMuted,
        fontSize: "12px",
        marginBottom: "12px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      <span style={{ color: theme.colors.brand }}>{icon}</span> {label}
    </div>
    <div
      style={{
        fontSize: "28px",
        fontWeight: 800,
        color: theme.colors.textMain,
      }}
    >
      {value}
    </div>
    {sub && (
      <div
        style={{
          fontSize: "12px",
          color: theme.colors.textMuted,
          marginTop: "6px",
        }}
      >
        {sub}
      </div>
    )}
  </div>
);

const AccountRow = ({ acc, i, total }) => (
  <div
    style={{
      padding: "16px 20px",
      borderBottom:
        i === total - 1 ? "none" : `1px solid ${theme.colors.border}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: acc.isTarget ? theme.colors.highlight : "transparent",
    }}
  >
    <div>
      <div
        style={{
          fontWeight: 700,
          fontSize: "15px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: theme.colors.textMain,
        }}
      >
        {acc.company}{" "}
        {acc.isTarget && <CheckCircle2 size={14} color={theme.colors.accent} />}
      </div>
      <div
        style={{
          fontSize: "12px",
          color: theme.colors.textMuted,
          marginTop: "4px",
        }}
      >
        {acc.leads} Leads • {Array.from(acc.signals).slice(0, 2).join(", ")}
      </div>
    </div>
    <div style={{ display: "flex", gap: "6px" }}>
      {Array.from(acc.functions).map((f) => (
        <span
          key={f}
          style={{
            fontSize: "10px",
            padding: "3px 10px",
            backgroundColor: "#fff",
            border: `1px solid ${theme.colors.border}`,
            borderRadius: "6px",
            fontWeight: 700,
            color: theme.colors.textMain,
          }}
        >
          {f}
        </span>
      ))}
    </div>
  </div>
);

const CoverageList = ({ title, targets, actuals }) => (
  <div>
    <h3
      style={{
        fontSize: "14px",
        fontWeight: 700,
        color: theme.colors.textMuted,
        marginBottom: 16,
        textTransform: "uppercase",
      }}
    >
      {title}
    </h3>
    <div style={ListWrapperStyle}>
      {targets.map((t, idx) => {
        const match = actuals.find(
          (a) => a.company.toLowerCase() === t.name.toLowerCase()
        );
        return (
          <div
            key={idx}
            style={{
              padding: "12px 16px",
              borderBottom: `1px solid ${theme.colors.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                fontWeight: match ? 700 : 500,
                color: match ? theme.colors.textMain : theme.colors.textMuted,
              }}
            >
              {t.name}
            </div>
            {match ? (
              <CheckCircle2 size={16} color={theme.colors.success} />
            ) : (
              <Circle size={16} color={theme.colors.border} />
            )}
          </div>
        );
      })}
    </div>
  </div>
);

const PriorityCard = ({ priorities }) => (
  <div
    style={{
      padding: "24px",
      border: `1px solid ${theme.colors.border}`,
      borderRadius: "20px",
      marginBottom: 24,
    }}
  >
    <h2
      style={{
        fontSize: "15px",
        fontWeight: 700,
        marginBottom: "20px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <PieChart size={20} color={theme.colors.brand} /> Investment Signals
    </h2>
    {priorities.slice(0, 5).map((t, i) => (
      <div key={i} style={{ marginBottom: "18px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "12px",
            marginBottom: "8px",
            fontWeight: 600,
          }}
        >
          <span>{t.theme}</span>
          <span style={{ color: theme.colors.brand }}>{t.count} Leads</span>
        </div>
        <div
          style={{
            height: "8px",
            backgroundColor: theme.colors.surface,
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${t.pct}%`,
              backgroundColor: theme.colors.brand,
              borderRadius: "4px",
            }}
          />
        </div>
      </div>
    ))}
  </div>
);

const InsightCard = ({ multiLeadPct }) => (
  <div
    style={{
      padding: "24px",
      backgroundColor: theme.colors.surface,
      borderRadius: "20px",
      border: `1px solid ${theme.colors.border}`,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 12,
      }}
    >
      <Activity size={18} color={theme.colors.brand} />
      <h2 style={{ fontSize: "15px", fontWeight: 700, margin: 0 }}>
        Strategic Data Insight
      </h2>
    </div>
    <p
      style={{
        fontSize: "13px",
        color: theme.colors.textMuted,
        lineHeight: "1.6",
        margin: 0,
      }}
    >
      Account penetration is accelerating. Approximately{" "}
      <span style={{ color: theme.colors.textMain, fontWeight: 700 }}>
        {multiLeadPct}%
      </span>{" "}
      of engaged accounts now have more than one active stakeholder, signaling
      cross-functional buy-in.
    </p>
  </div>
);

/* --- STYLES --- */
const SectionHeaderStyle = {
  fontSize: "13px",
  fontWeight: 700,
  color: theme.colors.textMuted,
  textTransform: "uppercase",
  letterSpacing: "1.2px",
  marginBottom: "16px",
};
const ListWrapperStyle = {
  border: `1px solid ${theme.colors.border}`,
  borderRadius: "16px",
  overflow: "hidden",
  backgroundColor: "#fff",
};
